type GraphQLErrorLike = {
  message?: string;
  path?: Array<string | number>;
  extensions?: {
    category?: string;
  };
};

const SUPPRESSED_GRAPHQL_MESSAGE_PATTERNS = [
  /^Invalid credentials\.$/i,
  /^Unauthenticated\.$/i,
  /^This action is unauthorized\.$/i,
  /Please input a job size\./i,
  /requires at least one product/i,
  /Please delete the Associated Employee record before deleting the user\./i,
];

const SUPPRESSED_GRAPHQL_CATEGORIES = new Set(["authentication", "authorization"]);

function isSuppressedGraphQLOperationMessage(
  operationName: string | null | undefined,
  message: string
): boolean {
  const normalizedOperationName = operationName?.trim() || "";

  if (normalizedOperationName === "validateLogin" && /^invalid$/i.test(message)) {
    return true;
  }

  return false;
}

export function parseGraphQLErrors(value: unknown): GraphQLErrorLike[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is GraphQLErrorLike => !!item && typeof item === "object");
  }

  if (typeof value !== "string") {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is GraphQLErrorLike => !!item && typeof item === "object")
      : [];
  } catch {
    return [];
  }
}

export function getGraphQLErrorCategory(error: GraphQLErrorLike): string {
  return error.extensions?.category?.trim().toLowerCase() || "unknown";
}

export function getGraphQLErrorPath(error: GraphQLErrorLike): string {
  return error.path?.length ? error.path.map(String).join(".") : "unknown";
}

export function isSuppressedGraphQLError(
  error: GraphQLErrorLike,
  operationName?: string | null
): boolean {
  const category = getGraphQLErrorCategory(error);
  const message = error.message?.trim() || "";

  if (SUPPRESSED_GRAPHQL_CATEGORIES.has(category)) {
    return true;
  }

  if (isSuppressedGraphQLOperationMessage(operationName, message)) {
    return true;
  }

  return SUPPRESSED_GRAPHQL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

export function getUnexpectedGraphQLErrors(
  errors: GraphQLErrorLike[],
  operationName?: string | null
): GraphQLErrorLike[] {
  return errors.filter((error) => !isSuppressedGraphQLError(error, operationName));
}

function getGraphQLErrorLevel(category: string): "error" | "warning" | "debug" {
  switch (category) {
    case "internal":
      return "error";
    case "validation":
    case "graphql":
      return "warning";
    default:
      return "debug";
  }
}

export function buildGraphQLErrorSentryEvent(
  errors: GraphQLErrorLike[],
  operationName?: string | null
): {
  fingerprint: string[];
  level: "error" | "warning" | "debug";
  message: string;
  tags: Record<string, string>;
  extra: Record<string, unknown>;
} | null {
  const unexpectedErrors = getUnexpectedGraphQLErrors(errors, operationName);
  if (!unexpectedErrors.length) {
    return null;
  }

  const primaryError = unexpectedErrors[0];
  const category = getGraphQLErrorCategory(primaryError);
  const path = getGraphQLErrorPath(primaryError);
  const normalizedOperationName = operationName?.trim() || "anonymous";
  const primaryMessage = primaryError.message?.trim() || "GraphQL error";

  return {
    fingerprint: ["graphql", normalizedOperationName, path, category],
    level: getGraphQLErrorLevel(category),
    message: `GraphQL ${category} ${normalizedOperationName} ${path}`,
    tags: {
      "graphql.operation": normalizedOperationName,
      "graphql.path": path,
      "graphql.category": category,
    },
    extra: {
      graphqlErrors: JSON.stringify(unexpectedErrors),
      graphqlErrorCount: unexpectedErrors.length,
      graphqlMessages: unexpectedErrors.map((error) => error.message || ""),
      graphqlPaths: unexpectedErrors.map(getGraphQLErrorPath),
      graphqlCategories: unexpectedErrors.map(getGraphQLErrorCategory),
      operationName: normalizedOperationName,
      primaryGraphQLErrorMessage: primaryMessage,
    },
  };
}
